import { PropertyFilter } from '@src/v3/interfaces/utils/http';
import { DEFAULT_FILTERS } from '@src/v3/constants/listingsFilter';

/**
 * Utility functions to check if specific filters have been applied/changed from defaults
 */

/**
 * Check if price filter has been changed from default values
 */
export const hasPriceFilterChanged = (filters: PropertyFilter): boolean => {
  return (
    filters.costMin !== DEFAULT_FILTERS.costMin ||
    filters.costMax !== DEFAULT_FILTERS.costMax
  );
};

/**
 * Check if rooms filter has been changed from default values
 */
export const hasRoomsFilterChanged = (filters: PropertyFilter): boolean => {
  const bedroomsChanged =
    (filters.bedrooms?.length || 0) > 0 ||
    (DEFAULT_FILTERS.bedrooms?.length || 0) > 0;
  const bathroomsChanged =
    (filters.bathrooms?.length || 0) > 0 ||
    (DEFAULT_FILTERS.bathrooms?.length || 0) > 0;
  return bedroomsChanged || bathroomsChanged;
};

/**
 * Check if amenities filter has been changed from default values
 */
export const hasAmenitiesFilterChanged = (filters: PropertyFilter): boolean => {
  return filters.amenity && filters.amenity.length > 0;
};

/**
 * Check if size filter has been changed from default values
 */
export const hasSizeFilterChanged = (filters: PropertyFilter): boolean => {
  return (
    filters.squareFootageMin !== DEFAULT_FILTERS.squareFootageMin ||
    filters.squareFootageMax !== DEFAULT_FILTERS.squareFootageMax
  );
};

/**
 * Check if sort filter has been changed from default values
 */
export const hasSortFilterChanged = (filters: PropertyFilter): boolean => {
  return filters.sortBy !== DEFAULT_FILTERS.sortBy;
};

/**
 * Check if keyword search filter has been changed from default values
 */
export const hasKeywordFilterChanged = (filters: PropertyFilter): boolean => {
  return filters.keyword !== DEFAULT_FILTERS.keyword;
};

/**
 * Check if easy apply filter has been changed from default values
 */
export const hasEasyApplyFilterChanged = (filters: PropertyFilter): boolean => {
  return filters.isEasyApply !== DEFAULT_FILTERS.isEasyApply;
};

/**
 * Check if move-in date filter has been changed from default values
 */
export const hasMoveInFilterChanged = (filters: PropertyFilter): boolean => {
  return filters.moveIn !== DEFAULT_FILTERS.moveIn;
};

/**
 * Check if no fee filter has been changed from default values
 */
export const hasNoFeeFilterChanged = (filters: PropertyFilter): boolean => {
  return filters.noFee !== DEFAULT_FILTERS.noFee;
};

/**
 * Check if any filter has been changed from default values
 * Useful for mobile filter drawer or overall filter state
 */
export const hasAnyFilterChanged = (filters: PropertyFilter): boolean => {
  return (
    hasPriceFilterChanged(filters) ||
    hasRoomsFilterChanged(filters) ||
    hasAmenitiesFilterChanged(filters) ||
    hasSizeFilterChanged(filters) ||
    hasSortFilterChanged(filters) ||
    hasKeywordFilterChanged(filters) ||
    hasEasyApplyFilterChanged(filters) ||
    hasMoveInFilterChanged(filters) ||
    hasNoFeeFilterChanged(filters)
  );
};

/**
 * Get a summary of which filters are currently active
 * Useful for debugging or analytics
 */
export const getActiveFilters = (filters: PropertyFilter): string[] => {
  const activeFilters: string[] = [];

  if (hasPriceFilterChanged(filters)) activeFilters.push('price');
  if (hasRoomsFilterChanged(filters)) activeFilters.push('rooms');
  if (hasAmenitiesFilterChanged(filters)) activeFilters.push('amenities');
  if (hasSizeFilterChanged(filters)) activeFilters.push('size');
  if (hasSortFilterChanged(filters)) activeFilters.push('sort');
  if (hasKeywordFilterChanged(filters)) activeFilters.push('keyword');
  if (hasEasyApplyFilterChanged(filters)) activeFilters.push('easyApply');
  if (hasMoveInFilterChanged(filters)) activeFilters.push('moveIn');
  if (hasNoFeeFilterChanged(filters)) activeFilters.push('noFee');

  return activeFilters;
};

/**
 * Count the number of active filters
 */
export const getActiveFiltersCount = (filters: PropertyFilter): number => {
  return getActiveFilters(filters).length;
};
